import { ReactNode } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { auth0Config } from "../authConfig";
import { SignInButton } from "./SignInButton";

const OsoLogo = () => (
  <svg width="56" height="26" viewBox="0 0 512 235" aria-hidden="true">
    <path
      fill="#5e85c4"
      d="M200.7,160.6l32.9,28.9-33.6,45.7h-45.6l-25.4-32.5,68.7-44.6,3,2.4ZM26.8,7.9L0,232.6h49.3l132.8-86.2L26.8,7.9ZM403.2,68.3l-99.3-51.7,75.4,216,23.9-164.3ZM45.8,0l264.6,232.5h49.4L281.1,13.8,45.8,0ZM507.2,151l.4,1,4.4-27.4-16.1-1.5,11.3,27.8ZM413.4,155.5l46.2-51.5,4.9-5.5-43.1-18.9-9.3,77.4,1.3-1.5ZM490.1,157.9l-16.9-41.6-46.2,51.5,63.1-9.9ZM427,50.2l-3.2,9.7,24.5,11.6-4-15.9-17.3-5.4Z"
    />
  </svg>
);

function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div className="auth-screen">
      <div className="auth-card">
        <OsoLogo />
        <div className="auth-title">OSO COMMAND</div>
        {children}
      </div>
    </div>
  );
}

/**
 * Gates the app behind Auth0 login: shows a loading state while the SDK
 * resolves, surfaces config/auth errors, and renders a sign-in screen until
 * the user is authenticated.
 */
export function AuthGuard({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated, error } = useAuth0();

  // Misconfiguration guard — a missing domain/clientId would otherwise produce
  // a confusing redirect failure.
  if (!auth0Config.domain || !auth0Config.clientId) {
    return (
      <AuthShell>
        <div className="auth-sub">Authentication is not configured.</div>
        <div className="auth-err">
          Set REACT_APP_AUTH0_DOMAIN and REACT_APP_AUTH0_CLIENT_ID in your .env,
          then restart the dev server.
        </div>
      </AuthShell>
    );
  }

  if (isLoading) {
    return (
      <AuthShell>
        <div className="auth-sub">Signing you in…</div>
        <div className="auth-spinner" />
      </AuthShell>
    );
  }

  if (error) {
    return (
      <AuthShell>
        <div className="auth-sub">Sign-in failed.</div>
        <div className="auth-err">{error.message}</div>
        <div style={{ marginTop: 16 }}>
          <SignInButton />
        </div>
      </AuthShell>
    );
  }

  if (!isAuthenticated) {
    return (
      <AuthShell>
        <div className="auth-sub">Mower Operations — Management View</div>
        <SignInButton />
      </AuthShell>
    );
  }

  return <>{children}</>;
}
